import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Paperclip, Trash2, FileText } from "lucide-react";
import {
  deletePatternAttachment,
  listPatternAttachments,
  uploadPatternAttachment,
} from "@/lib/api";

const ATTACHMENT_TYPES = [
  { value: "purchase_order", label: "Purchase order" },
  { value: "invoice",        label: "Invoice" },
  { value: "manifest",       label: "Manifest" },
  { value: "delivery_docket",label: "Delivery docket" },
  { value: "other",          label: "Other" },
];

const ACCEPT = "application/pdf,image/jpeg,image/png,image/webp";
const MAX_BYTES = 10 * 1024 * 1024;

export default function PatternAttachmentsPanel({ pattern }) {
  const [rows, setRows] = useState([]);
  const [type, setType] = useState("purchase_order");
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const refresh = () => setTick((k) => k + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await listPatternAttachments(pattern.id);
      if (cancelled) return;
      if (error) toast.error(error.message);
      setRows(data || []);
    })();
    return () => { cancelled = true; };
  }, [pattern.id, tick]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so same file can be picked twice
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error("File exceeds 10 MB.");
      return;
    }
    setBusy(true);
    const { error } = await uploadPatternAttachment({
      pattern,
      file,
      attachmentType: type,
    });
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Document attached.");
    refresh();
  };

  const onDelete = async (row) => {
    if (!window.confirm(`Delete "${row.file_name}"?`)) return;
    const { error } = await deletePatternAttachment(row);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Deleted.");
    refresh();
  };

  return (
    <div className="space-y-3" data-testid="pattern-attachments">
      <div className="rounded-md border bg-slate-50 px-3 py-3 space-y-2">
        <div className="text-xs text-slate-500">
          Documents attached here are inherited by every booking the pattern generates.
        </div>
        <div className="flex items-end gap-2">
          <div className="space-y-1 flex-1">
            <label className="text-[11px] uppercase tracking-wider text-slate-500">
              Document type
            </label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="pattern-attach-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ATTACHMENT_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <label
            className="inline-flex items-center gap-1.5 cursor-pointer rounded-md bg-slate-900 text-white px-3 py-2 text-sm hover:bg-slate-800 disabled:opacity-50"
            data-testid="pattern-attach-pick"
          >
            <Paperclip className="h-4 w-4" />
            {busy ? "Uploading…" : "Add file"}
            <input
              type="file"
              accept={ACCEPT}
              className="sr-only"
              onChange={onPick}
              disabled={busy}
            />
          </label>
        </div>
      </div>

      <div className="border rounded-md divide-y">
        {rows.length === 0 ? (
          <div className="py-8 text-center text-sm text-slate-400" data-testid="pattern-attach-empty">
            No documents attached yet.
          </div>
        ) : (
          rows.map((r) => (
            <div
              key={r.id}
              className="flex items-center gap-3 px-3 py-2 text-sm"
              data-testid={`pattern-attach-row-${r.id}`}
            >
              <FileText className="h-4 w-4 text-slate-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="truncate font-medium">{r.file_name}</div>
                <div className="text-[11px] text-slate-500">
                  {labelOf(r.attachment_type)} · {fmtSize(r.file_size)}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDelete(r)}
                data-testid={`pattern-attach-delete-${r.id}`}
              >
                <Trash2 className="h-4 w-4 text-rose-500" />
              </Button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function labelOf(v) {
  return ATTACHMENT_TYPES.find((t) => t.value === v)?.label || v;
}
function fmtSize(b) {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}
